import { debounce } from '@mui/material';
import { GridRowId, useGridApiContext } from '@mui/x-data-grid-premium';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { DEBOUNCE_DELAY_LOW } from 'src/lib/constants';
import { isCellExitKey } from 'src/utils/grid/cell/cellExit';

export type SearchCellOption<O> = O | string;

export type SearchCellValue<O, V> = SearchCellOption<O> | V | Array<SearchCellOption<O> | V> | null;

const isMultipleValue = <T>(value: T | readonly T[] | null | undefined): value is readonly T[] => Array.isArray(value);
const toMutableValue = <T>(value: T | readonly T[]): T | T[] => (isMultipleValue(value) ? [...value] : value);

/** The cell already holds a committed pick. An empty string is NOT one (free-solo, cleared). */
const hasSelection = <T>(value: T | readonly T[] | null | undefined): boolean =>
  isMultipleValue(value) ? value.length > 0 : Boolean(value);

interface UseSearchCellControlsProps<O, V> {
  id: GridRowId;
  field: string;
  value?: V | string | readonly (V | string)[] | null;
  /** Part of the shared picker props object; read by `SearchCell`/MUI, not by this hook. */
  getOptionLabel: (option: V) => string;
  transform?: (value: O) => V | null;
  isOptionEqualToValue: (option: O, value: V) => boolean;
  onChange?: (event: React.SyntheticEvent, value: SearchCellValue<O, V>, reason: string) => void;
  search: (text: string) => void;
  onOpen?: () => void;
  hasFocus?: boolean;
  multiple?: boolean;
  disableCloseOnSelect?: boolean;
  openOnFocus?: boolean;
  defaultSearchText?: string;
}

export function useSearchCellControls<O, V>({
  id,
  field,
  value,
  transform,
  onChange,
  search,
  onOpen,
  hasFocus,
  multiple,
  disableCloseOnSelect,
  openOnFocus = true,
  isOptionEqualToValue,
  defaultSearchText,
}: UseSearchCellControlsProps<O, V>) {
  const apiRef = useGridApiContext();
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  const handleSearch = useMemo(() => debounce(search, DEBOUNCE_DELAY_LOW), [search]);

  useEffect(() => () => handleSearch.clear(), [handleSearch]);

  /** Entering the cell opens the picker; leaving it closes it. Sibling of the same rule in `Autocomplete`. */
  useEffect(() => {
    if (hasFocus && openOnFocus) {
      setOpen(true);
      return;
    }

    if (!hasFocus) {
      setOpen(false);
      setFocused(false);
    }
  }, [hasFocus, openOnFocus]);

  const handleFocus = useCallback(() => {
    setFocused(true);
    if (openOnFocus) setOpen(true);
  }, [openOnFocus]);

  /** Closing hands the grid-wide query back to the next cell UNFILTERED. */
  const handleBlur = useCallback(() => {
    setOpen(false);
    search('');
  }, [search]);

  /**
   * The query is what the user TYPED — never a value read back to them.
   *
   *   first open  no value  ──▶ query ''             ──▶ full list ✓
   *   re-open     value set ──▶ query '' (not label) ──▶ full list ✓
   *   user types  'ac'      ──▶ query 'ac'           ──▶ filtered  ✓
   *
   * `search` is Algolia's `refine`, and ONE InstantSearch context wraps a whole
   * grid, so a seeded query outlives the cell that set it. Seeding it with
   * `getOptionLabel(value)` collapsed a re-entered picker to the one entity
   * already in the cell. `Autocomplete` draws this same line client-side with
   * `inputIsUserFilter`.
   *
   * `defaultSearchText` is the freeform-description typeahead (AI-ingested rows,
   * `ProductSelectCell`) and seeds ONLY a cell with nothing chosen yet.
   */
  const handleOpen = useCallback(
    (event: React.SyntheticEvent) => {
      if (!multiple) search(hasSelection(value) ? '' : (defaultSearchText ?? ''));

      onOpen?.();
      setOpen(true);
    },
    [defaultSearchText, multiple, onOpen, search, value]
  );

  const handleChange = useCallback(
    (
      event: React.SyntheticEvent,
      eventValue: SearchCellOption<O> | readonly SearchCellOption<O>[] | null,
      reason: string,
      details?: { option: SearchCellOption<O> }
    ) => {
      if (!disableCloseOnSelect) setOpen(false);

      let newValue: SearchCellValue<O, V>;
      if (!multiple) {
        const selectedOption = Array.isArray(eventValue) ? (eventValue[0] ?? null) : eventValue;
        newValue =
          selectedOption === null || typeof selectedOption === 'string'
            ? selectedOption
            : (transform?.(selectedOption) ?? selectedOption);
      } else if ((reason === 'selectOption' || reason === 'createOption') && details !== undefined) {
        const selectedValues = isMultipleValue(value) ? value : value ? [value] : [];
        const selectedOption = details.option;
        if (typeof selectedOption === 'string') {
          if (selectedValues.some((selectedValue) => selectedValue === selectedOption)) return;
          newValue = [...selectedValues, selectedOption];
        } else {
          if (
            selectedValues.some(
              (selectedValue) =>
                typeof selectedValue !== 'string' && isOptionEqualToValue(selectedOption, selectedValue)
            )
          )
            return;
          newValue = [...selectedValues, transform?.(selectedOption) ?? selectedOption];
        }
      } else {
        newValue = eventValue === null ? null : toMutableValue(eventValue);
      }

      apiRef.current.setEditCellValue({ id, field, value: newValue });
      onChange?.(event, newValue, reason);

      if (multiple) {
        setOpen(true);
        return;
      }

      // A cell-exit key belongs to the grid: close our own listbox and let the
      // keydown finish its trip so the row commits and focus moves.
      if (event.nativeEvent instanceof KeyboardEvent && isCellExitKey(event.nativeEvent)) {
        setOpen(false);
      } else {
        event.stopPropagation();
      }
    },
    [apiRef, disableCloseOnSelect, field, id, isOptionEqualToValue, multiple, onChange, transform, value]
  );

  return useMemo(
    () => ({ open, setOpen, focused, handleFocus, handleBlur, handleOpen, handleChange, handleSearch }),
    [focused, handleBlur, handleChange, handleFocus, handleOpen, handleSearch, open]
  );
}
